import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Login.css";
import { isTokenValid, readTokenClaims, saveSession } from "../../../app/auth";
import apiClient, { getFriendlyErrorMessage } from "../../../app/apiClient";

import LoadingIndicator from "../../../components/common/LoadingIndicator";

function Login({ setAuth, isPopup = false, onClose = () => {} }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const sessionExpired = new URLSearchParams(window.location.search).has(
      "sessionExpired",
    );
    const redirectMessage = sessionExpired
      ? "Your session has expired. Please log in again."
      : location.state?.message;

    if (redirectMessage) {
      setMessage(redirectMessage);
      setMessageType("error");
      // Keep the popup on the current public page.
    }
  }, [location.state]);

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateLoginForm = () => {
    if (!emailPattern.test(username.trim())) {
      setMessage("Please enter a valid email address.");
      setMessageType("error");
      return false;
    }

    if (password.trim().length < 4) {
      setMessage("Password must be at least 4 characters long.");
      setMessageType("error");
      return false;
    }

    return true;
  };


  const getClaimRole = (claims) => {
    const claim = claims.role || claims.roles || claims.authorities;
    if (Array.isArray(claim)) {
      const firstClaim = claim[0];
      return typeof firstClaim === "string"
        ? firstClaim
        : firstClaim?.authority;
    }
    return claim;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setMessage("");

    if (!validateLoginForm()) {
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const { data: responseData } = await apiClient.post("/auth/login", {
        username,
        password,
      });
      const data = responseData || {};

      const tokenResponse = data.data || data;
      const accessToken = String(tokenResponse.accessToken || "")
        .replace(/^Bearer\s+/i, "")
        .trim();
      const responseToken = String(tokenResponse.responseToken || "")
        .replace(/^Bearer\s+/i, "")
        .trim();
      const token =
        accessToken ||
        responseToken ||
        String(
          tokenResponse.access_token ||
            tokenResponse.token ||
            tokenResponse.jwt ||
            "",
        )
          .replace(/^Bearer\s+/i, "")
          .trim();
      if (!token) {
        setMessage("Login response did not include an authentication token.");
        setMessageType("error");
        return;
      }

      if (!isTokenValid(token)) {
        setMessage("The server returned an expired or invalid session. Please log in again.");
        setMessageType("error");
        return;
      }
      const tokenClaims = readTokenClaims(token);
      const responseUser = data.user || {};
      const responseUsername =
        tokenResponse.username ||
        data.username ||
        responseUser.username ||
        responseUser.email ||
        tokenClaims.sub ||
        username;
      const responseRole =
        tokenResponse.role ||
        data.role ||
        responseUser.role ||
        getClaimRole(tokenClaims);
      const responseEmail =
        tokenResponse.email || data.email || responseUser.email || username;

      const role = String(responseRole || "")
        .replace("ROLE_", "")
        .toUpperCase();
      if (!["ADMIN", "SUPERADMIN", "CLIENT"].includes(role)) {
        setMessage("Your account does not have a supported dashboard role.");
        setMessageType("error");
        return;
      }
      saveSession(token, role);
      localStorage.setItem("authResponse", JSON.stringify(data));
      localStorage.setItem("accessToken", accessToken || token);
      localStorage.setItem("responseToken", responseToken);
      localStorage.setItem("username", responseUsername);
      localStorage.setItem("userName", responseUsername);
      localStorage.setItem("email", responseEmail);
      localStorage.setItem("userEmail", responseEmail);

      setAuth(true);
      setMessage(data.message || "Login successful.");
      setMessageType("success");

      if (isPopup) {
        onClose();
      }

      if (role === "ADMIN" || role === "SUPERADMIN") {
        navigate("/admin/dashboard", { replace: true });
      } else if (role === "CLIENT") {
        navigate("/client/dashboard", { replace: true });
      } else {
        setMessage("Your account does not have a supported dashboard role.");
        setMessageType("error");
      }
    } catch (error) {
      console.error(error);
      setMessage(
        getFriendlyErrorMessage(error, "Login failed. Please try again."),
      );
      setMessageType("error");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const formContent = (
    <>
      <div className="form-header">
        <p className="eyebrow">Client portal</p>
        {/* <h2>Login</h2> */}
        {/* <p className="subheading">
          Access your project updates and dashboards in one place.
        </p> */}
      </div>

      <form onSubmit={handleLogin} className="login-form" aria-busy={submitting}>
        <div className="input-box">
          <label htmlFor="username">Email</label>
          <input
            id="username"
            disabled={submitting}
            type="email"
            placeholder="Enter your email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        <div className="input-box">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            disabled={submitting}
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button type="submit" className="login-btn" disabled={submitting}>
          {submitting ? <LoadingIndicator>Signing in...</LoadingIndicator> : "Sign In"}
        </button>
      </form>

      <p className={`message-box ${messageType}`} role={messageType === "error" ? "alert" : "status"}>{message}</p>
    </>
  );

  if (isPopup) {
    return (
      <div className="login-popup-overlay" onClick={() => { if (!submittingRef.current) onClose(); }}>
        <div className="login-popup-modal" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="login-close-btn"
            onClick={onClose}
            aria-label="Close login"
            disabled={submitting}
          >
            ×
          </button>

          <div className="login-popup-form">{formContent}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        {/* <div className="login-panel login-visual">
          <div className="brand-pill">Spirent</div>

          <div className="login-illustration-wrap">
            <img
              src={img}
              alt="Login Illustration"
              className="login-illustration"
            />
          </div>

          <div className="visual-copy">
            <span className="eyebrow">Client portal</span>
            <h1>Welcome back</h1>
            <p>
              Access your project updates, dashboards, and secure client tools
              in one place.
            </p>
          </div>
        </div> */}

        <div className="login-panel login-form-panel">{formContent}</div>
      </div>
    </div>
  );
}

export default Login;
